/** Per-account, per-draft, per-tab durable recovery. One tab never erases another. */
import { object, UUID, title, nonnegative } from "./core/workspace";
import { isRefusal, parseProject, type Project } from "./core/project";
export interface Recovery {
  readonly key: string; readonly owner: string; readonly id: string; readonly client: string;
  readonly title: string; readonly revision: number; readonly generation: number; readonly document: Project; readonly updatedAt: number;
}
function checked(value: unknown): Recovery | null {
  const row = object(value);
  if (!row || typeof row.key !== "string" || typeof row.owner !== "string" || typeof row.id !== "string" || !UUID.test(row.id)
    || typeof row.client !== "string" || !title(row.title) || !nonnegative(row.revision) || !nonnegative(row.generation) || !nonnegative(row.updatedAt)) return null;
  const document = parseProject(row.document);
  return isRefusal(document) ? null : { key: row.key, owner: row.owner, id: row.id, client: row.client, title: row.title,
    revision: row.revision, generation: row.generation, document, updatedAt: row.updatedAt };
}
function open(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open("subty-recovery", 1);
    request.onupgradeneeded = () => { const store = request.result.createObjectStore("drafts", { keyPath: "key" }); store.createIndex("draft", ["owner", "id"]); };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}
async function access<T>(mode: IDBTransactionMode, work: (store: IDBObjectStore) => IDBRequest<T>): Promise<T> {
  const db = await open();
  try {
    return await new Promise<T>((resolve, reject) => {
      const tx = db.transaction("drafts", mode); const request = work(tx.objectStore("drafts"));
      tx.oncomplete = () => resolve(request.result); tx.onabort = () => reject(tx.error); tx.onerror = () => reject(tx.error);
    });
  } finally { db.close(); }
}
export async function recoveries(owner: string, id: string): Promise<Recovery[]> {
  const values: unknown = await access("readonly", (store) => store.index("draft").getAll([owner, id]));
  return Array.isArray(values) ? values.map(checked).filter((row): row is Recovery => row !== null && row.owner === owner && row.id === id).sort((a, b) => b.updatedAt - a.updatedAt) : [];
}
export async function persist(value: Recovery): Promise<void> { await access("readwrite", (store) => store.put(value)); }
export async function forget(key: string): Promise<void> { await access("readwrite", (store) => store.delete(key)); }
/** A suspended tab may wake and write again after an ownership probe. Preserve newer work. */
export async function forgetUnchanged(snapshot: Recovery): Promise<void> {
  const db = await open();
  try {
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction("drafts", "readwrite"), store = tx.objectStore("drafts");
      const request = store.get(snapshot.key);
      request.onsuccess = () => {
        const current = checked(request.result);
        if (current && JSON.stringify(current) === JSON.stringify(snapshot)) store.delete(snapshot.key);
      };
      tx.oncomplete = () => resolve(); tx.onabort = () => reject(tx.error); tx.onerror = () => reject(tx.error);
    });
  } finally { db.close(); }
}
const identity = crypto.randomUUID();
const held = new Set<string>();
const channel = typeof BroadcastChannel === "undefined" ? null : new BroadcastChannel("subty-recovery-tabs");
channel?.addEventListener("message", (event: MessageEvent<unknown>) => {
  const value = object(event.data);
  if (value?.kind === "probe" && value.client === identity && typeof value.owner === "string" && typeof value.id === "string"
    && held.has(`${value.owner}:${value.id}`)) channel.postMessage({ kind: "alive", client: identity, probe: value.probe });
});
export function clientId(): string { return identity; }
export function claimRecovery(owner: string, id: string): void { held.add(`${owner}:${id}`); }
export function releaseRecovery(owner: string, id: string): void { held.delete(`${owner}:${id}`); }
/** Recover closed editors; an active tab keeps exclusive ownership of its own local work. */
export async function abandonedRecoveries(owner: string, id: string): Promise<Recovery[]> {
  const rows = await recoveries(owner, id);
  if (!channel || !rows.length) return rows;
  const probe = crypto.randomUUID(), active = new Set<string>();
  const receive = (event: MessageEvent<unknown>): void => {
    const value = object(event.data);
    if (value?.kind === "alive" && value.probe === probe && typeof value.client === "string") active.add(value.client);
  };
  channel.addEventListener("message", receive);
  try {
    for (const row of rows) channel.postMessage({ kind: "probe", probe, owner, id, client: row.client });
    await new Promise((resolve) => setTimeout(resolve, 75));
    return rows.filter((row) => !active.has(row.client));
  } finally { channel.removeEventListener("message", receive); }
}
